import { useEffect, useState } from 'react'

// A page's in-page sections as a Settings tree lists them (the guide's steps, the email templates, the component
// demos): where a jump lands, and which one the reader is in. A leaf module: the layouts' trees import it.

/** The nearest ancestor that scrolls on the y axis: the page's own pane on a phone, none (the document) on desktop. */
export const scrollParent = (element: HTMLElement): HTMLElement | null => {
  for (
    let parent = element.parentElement;
    parent && parent !== document.documentElement;
    parent = parent.parentElement
  ) {
    if (/auto|scroll/.test(getComputedStyle(parent).overflowY)) return parent
  }
  return null
}

export const goToSection = (id: string) => {
  document.getElementById('root-page')?.querySelector(`#${id}`)?.scrollIntoView({ behavior: 'smooth' })
  history.replaceState(null, '', `#${id}`)
}

/**
 * The section the reader is in: the last one whose top has reached the line a jump to it would land on (its scroll
 * margin). Off while `enabled` is false, so a tree only listens on the page its sections belong to.
 */
export function useSectionScrollspy(ids: readonly string[], enabled: boolean): string {
  const [active, setActive] = useState<string>(ids[0] ?? '')
  // A stable key, so a list rebuilt each render (the email templates) doesn't re-subscribe every time.
  const key = ids.join('|')

  useEffect(() => {
    if (!enabled) return
    const sectionIds = key.split('|').filter(Boolean)
    let frame = 0
    const update = () => {
      frame = 0
      // Scoped to the live page: a streamed Suspense segment can leave a hidden copy of these ids elsewhere.
      const root = document.getElementById('root-page') ?? document
      const sections = sectionIds.flatMap((id) => root.querySelector<HTMLElement>(`#${CSS.escape(id)}`) ?? [])
      const first = sections[0]
      if (!first) return
      const top = scrollParent(first)?.getBoundingClientRect().top ?? 0
      let current = first.id
      for (const section of sections) {
        const line = top + Number.parseFloat(getComputedStyle(section).scrollMarginTop) + 8
        if (section.getBoundingClientRect().top <= line) current = section.id
      }
      setActive(current)
    }
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(update)
    }
    window.addEventListener('scroll', schedule, { capture: true, passive: true })
    window.addEventListener('resize', schedule)
    schedule()
    return () => {
      window.removeEventListener('scroll', schedule, { capture: true })
      window.removeEventListener('resize', schedule)
      cancelAnimationFrame(frame)
    }
  }, [enabled, key])

  return active
}
